import { graphql, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';
import styled, {css} from 'styled-components';
import { Card, CardContent, Heading, InternalLink, Text, animation, colors, media } from '../../ui-components';

import InsightPropTypes from '../../models/Insight/InsightPropTypes';
import Authors from '../authors';
import BackgroundHero from '../background-hero';
import HeadingCategoryAndDate from '../heading-category-and-date';
import { truncateText } from '../../utils/truncate-text';
import DownloadableContentCard from "./downloadable-content-card";

const propTypes = {
  displayAuthors: PropTypes.bool,
  displayThumbnail: PropTypes.bool,
  headline: PropTypes.bool,
  insight: InsightPropTypes
};

export const useDefaultImage = () => {
  return useStaticQuery(graphql`
    query {
      defaultBG: file(relativePath: { eq: "default-banner.png" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 1200)
        }
      }
    }
  `);
};

/**
 * Component that renders a card containing insight information
 *
 * @component InsightCard
 * 
 * @param {object} props
 * 
 * @prop {string} className
 * @prop {object} insight
 * @prop {boolean} headline
 * @prop {boolean} displayThumbnail
 * @prop {boolean} displayAuthors
 */
const InsightCard = props => {
  const { className, insight, headline, displayThumbnail = true, displayAuthors = true } = props;
  const defaultBG = useDefaultImage();

  /**
   * Returns the JSX for the thumbnail image based on the displayThumbnail flag prop
   *
   * @function renderThumbnail
   * 
   * @returns {JSX.Element} The JSX for the card thumbnail
   */
  const renderThumbnail = () => {
    if (insight?.heroImageData?.url) {
      return (
        <ThumbBackgroundHeroImg
          display={displayThumbnail}
          headline={headline}
          src={insight?.heroImageData?.url}
          alt={insight?.heroImageData?.title}
        />
      );
    } else {
      return (
        <ThumbBackgroundHero
          display={displayThumbnail}
          headline={headline}
          fluid={
            insight?.heroImageData?.fluid
              ? insight?.heroImageData?.fluid
              : defaultBG?.defaultBG?.childImageSharp?.gatsbyImageData
          }
          alt={insight?.title || 'insight background image'}
        />
      );
    }
  };

  const renderAuthors = authors => {
    return <StyledAuthors condensed authors={authors} display={displayAuthors} />;
  };

  const renderDefaultInsightCard = () => {
    return (
      <FlexLink className={className} to={`/insights/${insight?.slug}`} title={`${insight?.title}`}>
        <FlexCard>
          {renderThumbnail()}
          <InsightCardContent>
            <div>
              <StyledHeadingCategoryAndDate insight={insight}/>
              <TitleHeading level={5}>{truncateText(insight?.title, 85)}</TitleHeading>
              <SnippetText size={'medium'}>{truncateText(insight?.snippet, 125)}</SnippetText>
            </div>
            {renderAuthors(insight?.authors)}
          </InsightCardContent>
        </FlexCard>
      </FlexLink>
    );
  };

  if (insight.type === 'Whitepaper' || insight.type === 'Ebook' || insight.type === 'Infographic') {
    return <DownloadableContentCard className={'downloadable-content-card'} insight={insight} />;
  } else {
    return renderDefaultInsightCard();
  }
};

InsightCard.propTypes = propTypes;

const FlexLink = styled(props => <InternalLink {...props} />)`
  display: flex;
  text-decoration: none;
  text-transform: none;
  overflow: hidden;
  width: 100%;
  height: 100%;
  align-items: inherit;
  box-shadow: Opx 0px 80px 0px rgba(58, 58, 58, 0.06);
`;

const ThumbBackgroundHeroBaseStyles = css`
  display: ${({ display }) => (display ? '' : 'none')};
  height: ${({ headline }) => (headline ? '280px' : '200px')};
  transition: transform ${animation.general.durationAndTiming};

  ${FlexLink}:hover & {
    transform: scale(1.035);
  }

  @media only screen and (max-width: ${media.smallTop}) {
    display: flex;
  }
`;

const ThumbBackgroundHero = styled(props => <BackgroundHero {...props} />)`
  ${ThumbBackgroundHeroBaseStyles}
`;

const ThumbBackgroundHeroImg = styled(props => <img {...props} />)`
  ${ThumbBackgroundHeroBaseStyles}
  object-fit: cover;
  margin-bottom: 0;
`;

const FlexCard = styled(props => <Card {...props} />)`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SnippetText = styled(props => <Text {...props} />)`
  margin-bottom: auto;
  width: 100%;
`;

const TitleHeading = styled(props => <Heading {...props} />)`
  margin: 4px 0 8px 0;
`;

const StyledAuthors = styled(props => <Authors {...props} />)`
  display: ${({ display }) => (display ? '' : 'none')};

  @media only screen and (max-width: ${media.smallTop}) {
    display: flex;
  }
`;

const StyledHeadingCategoryAndDate = styled(props => <HeadingCategoryAndDate {...props} />)``;

const InsightCardContent = styled(props => <CardContent {...props} />)`
  transition: background-color ${animation.general.durationAndTiming};
  padding: 28px;
  height: 100%;
  justify-content: space-between;

  ${FlexLink}:hover & {
    background-color: ${colors.cardBackgroundHover};
    transition: background-color ${animation.general.durationAndTiming};

    ${SnippetText}, ${TitleHeading} {
      color: ${colors.pureWhite};
      transition: color ${animation.general.durationAndTiming};
    }

    ${StyledAuthors} {
      p {
        color: ${colors.pureWhite};
        transition: color ${animation.general.durationAndTiming};
      }

      div,
      img {
        border-color: ${colors.cardBackgroundHover};
        transition: border-color ${animation.general.durationAndTiming};
      }
    }

    ${StyledHeadingCategoryAndDate}, p, path {
      color: ${colors.gray3};
      fill: ${colors.crederaRed};
      transition: color ${animation.general.durationAndTiming}, fill ${animation.general.durationAndTiming};
    }

    ${StyledHeadingCategoryAndDate}, circle {
      fill: ${colors.gray3};
    }
  }

  @media screen and (max-width: ${media.largeBottom}) {
    padding: 24px 20px;
  }
`;

export default InsightCard;
